import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { DexActivityTabView } from '@mark43/cad-api';
import { DexResultsView } from '@mark43/dex-api';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { globalAttributes } from '../../common/global-attributes';
import { useDexResults } from '../../data-exchange/results/use-dex-results';
import { getDexActivityQueryKey, useEventDexActivityQuery } from './use-event-dex-activity-query';

// Refresh every 30 seconds while no DEX notification is pending
const BACKGROUND_REFETCH_INTERVAL = 30_000;
// After a DEX notification, refresh every 5 seconds for at most 30 seconds
const NOTIFICATION_REFETCH_INTERVAL = 5000;
const NOTIFICATION_REFRESH_WINDOW = 30_000;

type NotificationRefresh = {
    eventId: number | undefined;
    // Number of entries cached when the first notification arrived
    knownEntryCount: number;
    // Results the notifications announced that are not cached yet
    pendingIncomingMessageIds: Set<number>;
    expiresAt: number;
};

// Ids of the DEX results in the activity. Inquiry entries have none.
function getIncomingMessageIds(data: DexActivityTabView | undefined): Set<number> {
    const incomingMessageIds = data?.dexActivityTabEntries.map(
        (entry) => entry.elasticUserAction.dexIncomingMessageId
    );
    return new Set(incomingMessageIds?.filter((id): id is number => typeof id === 'number'));
}

/**
 * True while a DEX notification for the event is still waiting for its new activity. Waits for every
 * announced result, because the inquiry entry often shows up first. A notification with no results
 * yet (a new inquiry) waits for any new entry.
 */
function isWaitingForNewActivity(
    refresh: NotificationRefresh | undefined,
    eventId: number | undefined,
    data: DexActivityTabView | undefined,
    now: number
): refresh is NotificationRefresh {
    // No notification for this event, or its window has ended
    if (refresh === undefined || refresh.eventId !== eventId || now >= refresh.expiresAt) {
        return false;
    }
    // Waits until every announced result is in the data
    if (refresh.pendingIncomingMessageIds.size > 0) {
        const receivedIncomingMessageIds = getIncomingMessageIds(data);
        const hasAllAnnouncedResults = Array.from(refresh.pendingIncomingMessageIds).every((id) =>
            receivedIncomingMessageIds.has(id)
        );
        return hasAllAnnouncedResults === false;
    }
    // No results announced yet (a new inquiry): waits until any new entry shows up
    const hasOnlyKnownEntries =
        (data?.dexActivityTabEntries.length ?? 0) <= refresh.knownEntryCount;
    return hasOnlyKnownEntries;
}

/**
 * Keeps the assigned event's DEX activity in the cache, so the First Responder DEX Activity tab still
 * shows it after the device loses connectivity. The open tab refreshes on its own every 5 seconds.
 *
 * In the background this refreshes every 30 seconds. After a DEX notification for the event, it
 * refreshes every 5 seconds until the new activity shows up, for at most 30 seconds.
 */
export function useAssignedEventDexActivityQuery(props: {
    eventId?: number;
    agencyTypeGlobalAttrId?: number;
}) {
    const { eventId, agencyTypeGlobalAttrId } = props;
    const { dataExchangeEventPanelTab, dexNextEnabled } = useEnabledFeatures();
    const { dexResultsListeners } = useDexResults();
    const queryClient = useQueryClient();
    const [notificationRefresh, setNotificationRefresh] = useState<NotificationRefresh>();

    const hasAssignedEvent = eventId !== undefined;
    // DEX Next shows a different list in the tab
    const isLegacyDex = dexNextEnabled === false;
    /*
     * The tab is only on police events. Still loads while the agency is unknown, so a failed event
     * details load can't leave the tab without offline data.
     */
    const isPoliceOrUnknownAgency =
        agencyTypeGlobalAttrId === undefined ||
        agencyTypeGlobalAttrId === globalAttributes.agencyTypeGlobal.POLICE;
    // Same checks that show this data in the DEX Activity tab
    const enabled =
        hasAssignedEvent && dataExchangeEventPanelTab && isLegacyDex && isPoliceOrUnknownAgency;

    const { refetch } = useEventDexActivityQuery({
        eventId,
        queryOptions: {
            enabled,
            refetchIntervalInBackground: true,
            // if a check fails, don't retry right away; wait for the next scheduled check
            retry: false,
            // Asked after every refresh: 5 seconds while waiting for a notification, otherwise 30
            refetchInterval: (data) =>
                isWaitingForNewActivity(notificationRefresh, eventId, data, Date.now())
                    ? NOTIFICATION_REFETCH_INTERVAL
                    : BACKGROUND_REFETCH_INTERVAL,
        },
    });

    useEffect(
        function refreshSoonAfterDexNotification() {
            if (enabled === false) {
                return undefined;
            }
            const queryKey = getDexActivityQueryKey(eventId);

            // Called with the results of every DEX notification (dex_nudge)
            function onDexResults(dexResultsViews: DexResultsView[]) {
                // Only results for queries linked to this event
                const eventResultsViews = dexResultsViews.filter(
                    (dexResultsView) => dexResultsView.query.agencyEventId === eventId
                );
                if (eventResultsViews.length === 0) {
                    return;
                }
                const now = Date.now();
                // Result ids already cached, and the ones this notification brought
                const data = queryClient.getQueryData<DexActivityTabView>(queryKey);
                const cachedIncomingMessageIds = getIncomingMessageIds(data);
                const announcedIncomingMessageIds = eventResultsViews
                    .flatMap((dexResultsView) => [
                        ...dexResultsView.results,
                        ...dexResultsView.cascadedQueries.flatMap((cascaded) => cascaded.results),
                    ])
                    .map((result) => result.incomingMessageId);

                /*
                 * Remember which new results to wait for. If an earlier notification is still
                 * waiting, keep waiting for its results too.
                 */
                setNotificationRefresh((previous) => {
                    const waiting = isWaitingForNewActivity(previous, eventId, data, now)
                        ? previous
                        : undefined;
                    const pendingIncomingMessageIds = [
                        ...Array.from(waiting?.pendingIncomingMessageIds ?? []),
                        ...announcedIncomingMessageIds,
                    ].filter((id) => cachedIncomingMessageIds.has(id) === false);
                    return {
                        eventId,
                        knownEntryCount:
                            waiting?.knownEntryCount ?? data?.dexActivityTabEntries.length ?? 0,
                        pendingIncomingMessageIds: new Set(pendingIncomingMessageIds),
                        expiresAt: now + NOTIFICATION_REFRESH_WINDOW,
                    };
                });

                // Refresh now unless the last refresh was less than 5 seconds ago
                const queryState = queryClient.getQueryState(queryKey);
                const lastRefreshedAt = Math.max(
                    queryState?.dataUpdatedAt ?? 0,
                    queryState?.errorUpdatedAt ?? 0
                );
                if (
                    queryState?.fetchStatus === 'idle' &&
                    now - lastRefreshedAt >= NOTIFICATION_REFETCH_INTERVAL
                ) {
                    refetch({ cancelRefetch: false });
                }
            }

            dexResultsListeners.add(onDexResults);
            return () => {
                dexResultsListeners.delete(onDexResults);
            };
        },
        [dexResultsListeners, enabled, eventId, queryClient, refetch]
    );
}
